import { Typography } from '@mui/material';
import moment from 'moment-timezone';
import React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { isValidDatetime } from '@woovi/dates';
import { CHARGE_EXPIRES_TYPE_ENUM, CHARGE_STATUS_ENUM } from '@woovi/shared';
import { AccountInfoLabel, BrowserOnly, Countdown, BoxFlex } from '@woovi/ui';

import type { PaymentLinkPaymentTerm_charge$key } from '../active/paymentLinkActive/__generated__/PaymentLinkPaymentTerm_charge.graphql.ts';

type PaymentLinkPaymentTermProps = {
  charge: PaymentLinkPaymentTerm_charge$key;
};
const PaymentLinkPaymentTerm = (
  props: PaymentLinkPaymentTermProps,
): ReactNode => {
  const { t } = useTranslation();

  const charge = useFragment<PaymentLinkPaymentTerm_charge$key>(
    graphql`
      fragment PaymentLinkPaymentTerm_charge on Charge {
        status
        expiresDate
        company {
          id
          preferences {
            chargeSettings {
              expiration {
                type
                fakeValue
              }
            }
          }
        }
        interestsSettings {
          daysAfterDueDate
        }
      }
    `,
    props.charge,
  );

  if (charge.status === CHARGE_STATUS_ENUM.COMPLETED) {
    return null;
  }

  if (!isValidDatetime(charge?.expiresDate)) {
    return null;
  }

  const { preferences } = charge.company;

  //* This logic is for the fake expiration timer, don't change the order of the variables, moment is mutable and will break the value logic
  const now = moment().tz('America/Sao_Paulo');
  const chargeCreatedAt = moment(charge.createdAt).tz('America/Sao_Paulo');

  const expirationFake = preferences?.chargeSettings?.expiration?.fakeValue;

  const expirationFakeMs = chargeCreatedAt.add(expirationFake, 'seconds');

  const getExpirationFromType = (): string => {
    if (
      charge?.company?.preferences?.chargeSettings?.expiration?.type ===
      CHARGE_EXPIRES_TYPE_ENUM.FAKE
    ) {
      const isFakeExpired = now.isAfter(expirationFakeMs);

      if (isFakeExpired) {
        return charge?.expiresDate;
      }

      return expirationFakeMs.toISOString();
    }

    return charge?.expiresDate;
  };
  //*

  const getPaymentTermText = (): ReactNode => {
    if (charge.status === CHARGE_STATUS_ENUM.EXPIRED) {
      return (
        <Typography color='error' sx={{ fontSize: '16px' }}>
          {t('Expired')}
        </Typography>
      );
    }

    if (charge.status === CHARGE_STATUS_ENUM.OVERDUE) {
      const date = moment(charge.expiresDate)
        .add(charge.interestsSettings.daysAfterDueDate, 'days')
        .format('DD/MM/YYYY');

      return (
        <Typography sx={{ textAlign: 'center', fontSize: '16px' }}>
          {t('Overdue charge, pay until {{ date }} with fines and interest', {
            date,
          })}
        </Typography>
      );
    }

    return (
      <BrowserOnly>
        <Countdown targetDate={getExpirationFromType()} />
      </BrowserOnly>
    );
  };

  return preferences?.chargeSettings?.expiration?.type ===
    CHARGE_EXPIRES_TYPE_ENUM.NONE ? null : (
    <BoxFlex sx={{ justifyContent: 'center', mt: '16px' }}>
      <AccountInfoLabel label={t('Payment Term')} text={getPaymentTermText()} />
    </BoxFlex>
  );
};

export default PaymentLinkPaymentTerm;
